import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { createColumnHelper } from '@tanstack/react-table';
import { Tag } from '@innovationnorway/cl-react';
import DateList from './DateList';
import { OneWayInOpportunityStatusDto, OneWayInServiceDto } from 'src/api/v2';

// Custom hook for service columns
export function useServiceColumns(history: OneWayInOpportunityStatusDto[]) {
  const { t: tOneWayIn } = useTranslation('oneWayIn');
  const recommendedServices: OneWayInServiceDto[] = useMemo(
    () => history.reduce((a, b) => a.concat(b.recommendedServices), [] as OneWayInServiceDto[]),
    [history],
  );
  const registeredServices: OneWayInServiceDto[] = useMemo(
    () => history.reduce((a, b) => a.concat(b.registeredServices), [] as OneWayInServiceDto[]),
    [history],
  );

  return useMemo(() => {
    const columnHelper = createColumnHelper<OneWayInServiceDto>();
    return [
      columnHelper.accessor((row) => row.name, {
        id: 'services',
      }),
      columnHelper.accessor((row) => row.id, {
        id: 'registeredRecommended',
        cell: (id) => {
          if (recommendedServices.find((value) => value.id === id.getValue()))
            return (
              <Tag data-color={'success' as const}>{tOneWayIn('customerFollowup.recommended' as any)}</Tag>
            );
          return (
            <Tag data-color={'neutral' as const}>{tOneWayIn('customerFollowup.notRecommended' as any)}</Tag>
          );
        },
      }),
      columnHelper.accessor((row) => row.id, {
        id: 'dateRecommended',
        maxSize: 20,
        cell: (id) => {
          const serviceRecommendationDates = history
            .filter((h) => h.recommendedServices.find((r) => r.id == id.getValue()))
            .map((s) => new Date(s.createdOn || new Date()));
          return <DateList dates={serviceRecommendationDates} />;
        },
      }),
      columnHelper.accessor((row) => row.id, {
        id: 'registeredDelivered',
        maxSize: 20,
        cell: (id) => {
          if (registeredServices.find((value) => value.id === id.getValue()))
            return (
              <Tag data-color={'success' as const}>
                {tOneWayIn('customerFollowUp.deliveredServices.delivered' as any)}
              </Tag>
            );
          return (
            <Tag data-color={'neutral' as const}>{tOneWayIn('customerFollowUp.notDelivered' as any)}</Tag>
          );
        },
      }),
      columnHelper.accessor((row) => row.id, {
        id: 'dateDelivered',
        cell: (id) => {
          const serviceDeliveryDates = history
            .filter((h) => h.deliveredOn && h.registeredServices.find((r) => r.id == id.getValue()))
            .map((s) => new Date(s.deliveredOn || new Date()));
          return <DateList dates={serviceDeliveryDates} />;
        },
      }),
    ];
  }, [registeredServices, recommendedServices, history, tOneWayIn]);
}
